// core
import Vue from 'vue';
import Vuelidate from 'vuelidate';
import store from './store';
import router from '@/router/router';
import config from './config';
import {
  BASE_URL,
  ENV,
  isProduction,
} from '@inscribe/ui-kit/src/utils/environment';
import UIKit from '@inscribe/ui-kit';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import VueIntercom from 'vue-intercom';
import VuePhoneNumberInput from 'vue-phone-number-input';
import VTooltip from 'v-tooltip';
import vSelect from 'vue-select';
import Notifications from 'vue-notification';
import VueMq from 'vue-mq';
import BootstrapVue from 'bootstrap-vue';

// services
import ApiService from '@/shared/services/api.service';

// V2
import 'vue-select/dist/vue-select.css';
import VCalendar from 'v-calendar';
import VueWaypoint from 'vue-waypoint';

// utils
import cookieConsentManager from '@/utils/cookieConsentManager';
import { logError } from '@/utils/error';

// Stylesheets
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import '@inscribe/ui-kit/styles/index.scss';
import '@inscribe/signal/dist/signal.min.css';
import '../styles/custom.scss';

// third party
import datadogRumManager from '@/third-party/datadogRumManager';
import { faInit } from '@/third-party/fontawesome';

// components
import App from '@/App.vue';

// directives
import { initDirectives } from '@/V2/directives/directives';
initDirectives(Vue);

// config
Vue.config.devtools = true;
Vue.config.debug = true;
// catch uncaught errors in the app and log them to Sentry
Vue.config.errorHandler = function (err) {
  // eslint-disable-next-line no-console
  console.error(err);
  logError(err);
};
const CONFIG = config[ENV];

faInit();
ApiService.init(BASE_URL);

cookieConsentManager.init(() => {
  // aws signature, pusher stats session
  datadogRumManager.cleaners.params = ['Signature', 'session'];
  datadogRumManager.init({
    applicationId: CONFIG.datadog_rum.applicationId,
    clientToken: CONFIG.datadog_rum.clientToken,
    service: 'inscribe-frontend',
    env: ENV,
    version: document.getElementById('app-version')?.innerText,
    trackUserInteractions: true,
    enableExperimentalFeatures: ['clickmap'],
    actionNameAttribute: 'class',
  });
});
if (isProduction) {
  Sentry.init({
    Vue,
    dsn: 'https://232fc7155ea34f31b7a781b2d2d62aa7@sentry.io/1384448',
    environment: ENV,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error && error.name === 'NavigationDuplicated') {
        event.fingerprint = ['NavigationDuplicated'];
      }
      return event;
    },
  });
  Vue.use(VueIntercom, { appId: 'c7so6toh' });
}

Vue.use(VCalendar, {
  firstDayOfWeek: 2,
  datePickerShowDayPopover: false,
  formats: {
    title: 'MMMM YYYY',
    weekdays: 'W',
    navMonths: 'MMM',
    input: ['DD/MM/YY', 'YYYY-MM-DD', 'YYYY/MM/DD'],
    dayPopover: 'L',
    data: ['L', 'YYYY-MM-DD', 'YYYY/MM/DD'],
  },
});
Vue.use(VueWaypoint);
Vue.use(UIKit);
Vue.use(BootstrapVue);
Vue.use(Notifications);
Vue.use(require('vue-moment'));
Vue.use(VTooltip);
Vue.use(Vuelidate);
Vue.use(VueMq, {
  breakpoints: {
    mobile: 500,
    small0: 768,
    small1: 900,
    small2: 1240,
    small3: 1424,
    mediumLarge: 1548,
    lg: Infinity,
  },
});

Vue.component('vue-phone-number-input', VuePhoneNumberInput);
Vue.component('v-select', vSelect);

new Vue({
  router: router,
  store,
  render: (h) => h(App),
}).$mount('#app');
